import { action, computed, observable } from 'mobx';
import objectPath from 'object-path';
import Alert from 'react-s-alert';
import { isFunction } from 'lodash';

import { AppManager, Variables, Input } from 'core/index';

import { headerStore } from 'apps/components/header/app';
import { clientStore } from 'apps/client/app';
import { PaymentSpaceApp } from 'apps/payment/app';
import { SubscriptionAppManager } from 'apps/payment/subscription/appManager';
import { InviteApp } from 'apps/group/app';
import { InitiativeModel, InitiativeApp } from 'apps/initiative/app';
import {
  InitiativeQuery,
  InitiativeMutation,
  InitiativeStripeCustomer,
  InitiativeBillingSubscriptionQuery,
  ExtendFreeTrialMutation,
} from 'apps/initiative/gql/index';
import {
  UpdatePromotionMutation,
  UpdateSpaceFlagMutation,
} from 'apps/profile/gql';

import { isNumber } from 'apps/utils';

const MAX_FREE_SEATS = 100;
const MAX_DISCOUNT = 100;

export class InitiativeAppManager extends AppManager {
  static registryName = 'InitiativeAppManager';

  @observable billingDialogVisible = false;

  @observable seats;

  @observable discount;

  @observable updatingBilling = false;
  @observable updatingFlags = false;
  @observable featureDialogVisible = false;

  @observable subscriptionQuery = null;

  @observable freeSeatsPreview = 0;
  @observable discountPreview = 0;
  @observable earlyAccess = false;
  @observable freeTrialExtending = false;

  constructor(parent, init = {}) {
    super(parent, init);
    this.AppRef('initiative', InitiativeApp);

    this.variables = new Variables({
      slug: () => this.routerStore.params.get('initiative'),
    });

    this.initiativeInput = new Input({
      id: () => this.initiative.id,
      slug: () => this.initiative.slug,
      avatar: () => {
        if (this.initiative.avatar) {
          return this.initiative.avatar.id;
        }
        return null;
      },
      title: () => this.initiative.title,
      description: () => this.initiative.description,
      tags: () =>
        this.initiative.tags.map(tag => ({
          id: tag.id,
          name: tag.name,
          deleted: tag.deleted,
        })),
      socialAccounts: () =>
        this.initiative.socialAccounts.map(social => ({
          id: social.id,
          accountType: social.accountType,
          accountName: social.accountName,
          deleted: social.deleted,
        })),
    });

    this.variables.onChange(['slug'], this.newSlug);
  }

  @action
  QueryBillingSubscription = () => {
    const variables = new Variables({
      spaceSlug: this.initiative.slug,
    });

    this.subscriptionQuery = new InitiativeBillingSubscriptionQuery(this, {
      variables,
    });

    this.subscriptionQuery.process(
      initiative => InitiativeModel.Load(initiative),
      result => {
        if (!result) {
          return;
        }
        this.InitiativeUpdate(result);
        if (result.promotion) {
          this.SetDiscountPreviewNumber(result.promotion.discount);
          this.SetFreeSeatsPreviewNumber(result.promotion.freeSeats);
          this.SetEarlyAccess(result.promotion.earlyAccess);
        }
      },
    );

    this.subscriptionQuery.query();
  };

  @computed
  get loading() {
    return this.queries.get('initiative')?.loading;
  }

  @action
  newSlug = () => {
    this.NewInitiativeQuery();
  };

  @action
  ExtendFreeTrial = (trialEnd, callback) => {
    const input = new Input({
      spaceId: () => this.initiative.inSpace.id,
      trialEnd: () => `${trialEnd}`,
    });
    this.freeTrialExtending = true;

    const mutation = new ExtendFreeTrialMutation(
      input,
      null,
      {
        onSuccess: () => {
          this.freeTrialExtending = false;
          callback?.();
          Alert.success('Free trial extended successfully');
        },
        onSysErr: error => {
          this.freeTrialExtending = false;
          Alert.error(error?.message);
        },
        onUserErr: error => {
          this.freeTrialExtending = false;
          Alert.error(error?.message);
        },
      },
      true,
    );

    mutation.mutate();
  };

  @computed
  get pendingInvitation() {
    const { pendingInvitations } = clientStore;
    const inSpaceID = objectPath.get(this, 'initiative.inSpace.id');
    if (!inSpaceID || !pendingInvitations) return null;
    const invite = pendingInvitations.find(
      ({ group }) => objectPath.get(group, 'space.id') === inSpaceID,
    );
    return invite ? new InviteApp(null, { id: invite.id }) : null;
  }

  @action
  NewInitiativeQuery = () => {
    const q = new InitiativeQuery(this, {
      queryKey: 'initiative',
      onSysErr: this.onInitiativeError,
    });
    q.process(invites => invites, clientStore.SetPendingInvitations, {
      subPath: 'loggedInUser.pendingInvites',
    });
    q.process(
      initiative => InitiativeModel.Load(initiative),
      this.InitiativeUpdate,
      { subPath: 'initiative' },
    );
    q.query();
  };

  @action
  onInitiativeError = () => {
    Alert.error('Hmm...We failed to load this initiative. Please try again.');
  };

  @action
  InitiativeUpdate = initiativeModel => {
    this.initiative = initiativeModel;
    if (this.initiative) {
      headerStore.SetPage(initiativeModel);
    }
  };

  @action
  SubmitInitiative = options => {
    this.mutations.push(InitiativeMutation.New(this.initiativeInput, options));
  };

  // FIXME This code is repeated at least in apps/profile/main/appManager
  @action Subscribe = params => {
    const subscriptionAppManager = new SubscriptionAppManager(this, params);
    const paymentSpace = new PaymentSpaceApp(subscriptionAppManager, params);
    if (!paymentSpace || (paymentSpace && !paymentSpace.preview)) {
      subscriptionAppManager.NewSubscriptionQuery({
        preview: true,
      });
    }
    paymentSpace.ConfirmSubscribe();
  };

  @action
  CreateStripeCustomer = () => {
    this.mutations.push(InitiativeStripeCustomer.New(this.initiativeInput));
  };

  @computed get cadDiff() {
    if (!this.initiative.inSpace.flags) return null;

    return JSON.parse(this.initiative.inSpace.flags).cadDiff;
  }

  @computed get featureFlags() {
    return JSON.parse(this.initiative.inSpace.flags || '{}');
  }

  @action HandleFeatureFlagsByKey(featureKey, value) {
    const flags = JSON.parse(this.initiative.inSpace.flags || '{}');

    flags[featureKey] = value;

    return flags;
  }

  @action UpdateFeatureFlags = option => {
    const { key, value, onSuccess, onError } = option || {};
    this.updatingFlags = true;

    const saveFlags = this.HandleFeatureFlagsByKey(key, value);

    const input = new Input({
      spaceId: this.initiative.inSpace.id,
      flags: JSON.stringify(saveFlags),
    });

    const mutation = UpdateSpaceFlagMutation.New(input, {
      onSuccess: result => {
        this.updatingFlags = false;
        if (isFunction(onSuccess)) {
          onSuccess(result);
        }
      },
      onSysErr: () => {
        Alert.error('There was an error while updating flags');
        this.updatingFlags = false;
        if (isFunction(onError)) {
          onError();
        }
      },
      onUserErr: () => {
        Alert.error('There was an error while updating flags');
        this.updatingFlags = false;
        if (isFunction(onError)) {
          onError();
        }
      },
    });

    return mutation;
  };

  @action SetFeatureDialogVisible = visible => {
    this.featureDialogVisible = visible;
  };

  @action SetBillingDialogVisible = visible => {
    this.billingDialogVisible = visible;
  };

  @action SetDiscount = value => {
    const val = value ? Number(value) : '';
    if (val < 0 || val > MAX_DISCOUNT) {
      return;
    }

    this.discount = val;
    this.seats = '';
  };

  @action SetSeats = value => {
    const val = value ? Number(value) : '';

    if (val < 0 || val > MAX_FREE_SEATS) {
      return;
    }

    this.seats = val;
    this.discount = '';
  };

  @action SubmitPromotionData = () => {
    const data = {};

    if (isNumber(this.seats)) {
      data.freeSeats = this.seats;
    }

    if (isNumber(this.discount)) {
      data.discount = this.discount;
    }

    const input = new Input({
      spaceId: this.initiative.inSpace.id,
      ...data,
    });

    this.updatingBilling = true;

    const mutation = UpdatePromotionMutation.New(input, {
      onSuccess: result => {
        Alert.success("User's billing has been updated successfully");
        this.updatingBilling = false;

        this.SetFreeSeatsPreviewNumber(result.freeSeats);
        this.SetDiscountPreviewNumber(result.discount);
      },
      onSysErr: () => {
        Alert.error('There was an error while updating user billing');
        this.updatingBilling = false;
      },
      onUserErr: () => {
        Alert.error('There was an error while updating user billing');
        this.updatingBilling = false;
      },
    });
    return mutation;
  };

  @action SetFreeSeatsPreviewNumber(freeSeats) {
    if (isNumber(freeSeats)) {
      this.freeSeatsPreview = freeSeats;
      return;
    }

    this.freeSeatsPreview =
      this.initiative.billingSubscriptions.apps?.[0]?.seats?.freeSeats || 0;
  }

  @action SetDiscountPreviewNumber(discount) {
    if (isNumber(discount)) {
      this.discountPreview = discount;
      return;
    }

    this.discountPreview =
      this.initiative.billingSubscriptions.apps?.[0]?.seats?.discount || 0;
  }

  @action SetEarlyAccess = value => {
    this.earlyAccess = String(value);
  };
}
